// values and helpers exported for the frontend

export const defaultPositionFields: Array<PositionFieldConfig> = [
    {
        key: "articleNumber1",
        source: "PRODUCT",
        labelGerman: "Artikelnummer",
        labelEnglish: "Article number",
        visibleWhenEmpty: false,
    },
    {
        key: "articleNumber2",
        source: "PRODUCT",
        labelGerman: "Artikelnummer 2",
        labelEnglish: "Article number 2",
        visibleWhenEmpty: false,
    },
    {
        key: "articleNumber3",
        source: "PRODUCT",
        labelGerman: "Artikelnummer 3",
        labelEnglish: "Article number 3",
        visibleWhenEmpty: false,
    },
    {
        key: "articleNumber4",
        source: "PRODUCT",
        labelGerman: "Artikelnummer 4",
        labelEnglish: "Article number 4",
        visibleWhenEmpty: false,
    },
    {
        key: "customerArticleNumbers",
        labelEnglish: "Customer Art.Nr.",
        labelGerman: "Kunden Art.Nr.",
        source: "PRODUCT",
        visibleWhenEmpty: false,
    },
    {
        key: "color",
        source: "PRODUCT",
        labelGerman: "Farbe",
        labelEnglish: "Color",
        visibleWhenEmpty: false,
    },
    {
        key: "material",
        source: "PRODUCT",
        labelGerman: "Material",
        labelEnglish: "Material",
        visibleWhenEmpty: false,
    },
    {
        key: "dimensions",
        source: "PRODUCT",
        labelGerman: "Abmessungen",
        labelEnglish: "Dimensions",
        visibleWhenEmpty: false,
    },
    {
        key: "size",
        source: "PRODUCT",
        labelGerman: "Größe",
        labelEnglish: "Size",
        visibleWhenEmpty: false,
    },
];

export const defaultCustomerFields: Array<CustomerFieldConfig> = [
    {
        key: "name",
        source: "BILLING_PARTNER",
        labelEnglish: "Name",
        labelGerman: "Name",
        visibleWhenEmpty: true,
    },
];

import { equalsIgnoreCase, round, assert } from "@repo/utils";
import type { CustomerFieldConfig, PositionFieldConfig } from "./types.js";

interface UOM {
    code: string;
    conversionFactors?: Record<string, number> | null;
}

interface CalcInput {
    unit: UOM | null;
    quantity: number;
}

interface Product {
    unitsOfMeasure?: UOM[] | null;
}

export function calculateQuantityByUnitCodes<T extends CalcInput>(
    input: T,
    product: Product | null,
    units: UOM[],
): T & { byUnitCode: Record<string, number> } {
    const from = input.unit?.code;
    if (!from) {
        return {
            ...input,
            byUnitCode: {},
        };
    }

    const conversionFactors =
        product?.unitsOfMeasure?.find(x => equalsIgnoreCase(x.code, from))?.conversionFactors ??
        units.find(x => equalsIgnoreCase(x.code, from))?.conversionFactors;

    function convert(q: number, to: string): number | null {
        assert(from);
        if (from === to) {
            return q;
        }

        const factor = conversionFactors?.[to];
        if (factor === undefined) {
            return null;
        }

        return roundQuantity(q * factor);
    }

    const byUnitCode = (product?.unitsOfMeasure && product.unitsOfMeasure.length > 0 ? product.unitsOfMeasure : units)
        .map(x => ({
            code: x.code,
            quantity: convert(input.quantity, x.code),
        }))
        .reduce(
            (acc, cur) => {
                if (cur.quantity !== null) {
                    acc[cur.code] = cur.quantity;
                }
                return acc;
            },
            {} as Record<string, number>,
        );

    return {
        ...input,
        byUnitCode,
    };
}

function roundQuantity(q: number) {
    return round(q, 4);
}
